/**
 * Editorial pick field on v2-events documents.
 * Multi-select values; unset / empty when cleared.
 * Rank (low → high): day < month < week < saveTheDate < season.
 * Cascade: season ⇒ saveTheDate + week ⇒ month ⇒ day.
 *   saveTheDate ⇒ month ⇒ day (independent of week — picking one does not set the other).
 *   week ⇒ month ⇒ day (independent of saveTheDate). month ⇒ day.
 * Season is stored as summer/fall/winter/spring (inferred from event date on write).
 * Admin UI uses a single "Best of season" toggle; public shows the specific season label.
 * Picking "Best of season" also flags `saveTheDate` — every season pick now also
 * appears in the homepage "Save the date" section (see `isHomepageFeaturedEditorialPick`).
 * Legacy `year` / `sff` in Firestore are ignored on read and not written.
 * Legacy single-string 'day' | 'month' still accepted.
 */

import { compareEventsUpcomingThenPast, getEventCalendarDay } from '../shared/dateUtils';

export const EDITORIAL_PICK = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  SUMMER: 'summer',
  FALL: 'fall',
  WINTER: 'winter',
  SPRING: 'spring',
  SAVE_THE_DATE: 'saveTheDate',
};

export const SAVE_THE_DATE_LABEL = 'Save the date';

/** Admin toggle label for any stored season value. */
export const ADMIN_SEASON_LABEL = 'Best of season';

/** @type {Array<'summer' | 'fall' | 'winter' | 'spring'>} */
export const EDITORIAL_PICK_SEASONS = [
  EDITORIAL_PICK.SUMMER,
  EDITORIAL_PICK.FALL,
  EDITORIAL_PICK.WINTER,
  EDITORIAL_PICK.SPRING,
];

const PICK_LABELS = {
  [EDITORIAL_PICK.DAY]: 'Best of day',
  [EDITORIAL_PICK.WEEK]: 'Best of the week',
  [EDITORIAL_PICK.MONTH]: 'Best of the month',
  [EDITORIAL_PICK.SUMMER]: 'Best of summer',
  [EDITORIAL_PICK.FALL]: 'Best of fall',
  [EDITORIAL_PICK.WINTER]: 'Best of winter',
  [EDITORIAL_PICK.SPRING]: 'Best of spring',
  [EDITORIAL_PICK.SAVE_THE_DATE]: SAVE_THE_DATE_LABEL,
};

const VALID_PICKS = new Set([
  EDITORIAL_PICK.DAY,
  EDITORIAL_PICK.WEEK,
  EDITORIAL_PICK.MONTH,
  EDITORIAL_PICK.SAVE_THE_DATE,
  ...EDITORIAL_PICK_SEASONS,
]);

/** Rank: season > saveTheDate > week > month > day */
const PICK_SORT_SCORE = {
  [EDITORIAL_PICK.DAY]: 1,
  [EDITORIAL_PICK.MONTH]: 2,
  [EDITORIAL_PICK.WEEK]: 3,
  [EDITORIAL_PICK.SAVE_THE_DATE]: 4,
  [EDITORIAL_PICK.SUMMER]: 5,
  [EDITORIAL_PICK.FALL]: 5,
  [EDITORIAL_PICK.WINTER]: 5,
  [EDITORIAL_PICK.SPRING]: 5,
};

/** Admin toggles: day / month / Best of season / Save the date (week hidden). */
export const EDITORIAL_PICK_OPTIONS = [
  { value: EDITORIAL_PICK.DAY, label: PICK_LABELS[EDITORIAL_PICK.DAY] },
  { value: EDITORIAL_PICK.MONTH, label: PICK_LABELS[EDITORIAL_PICK.MONTH] },
  { value: 'season', label: ADMIN_SEASON_LABEL },
  { value: EDITORIAL_PICK.SAVE_THE_DATE, label: PICK_LABELS[EDITORIAL_PICK.SAVE_THE_DATE] },
];

/** Category-toggle style options for admin pick buttons. */
export const EDITORIAL_PICK_TOGGLE_OPTIONS = EDITORIAL_PICK_OPTIONS.map(({ label }) => ({
  label,
  color: '#A8D5BA',
}));

/**
 * Public label for a stored pick value.
 * @param {string} pick
 * @returns {string}
 */
function labelForPick(pick) {
  return PICK_LABELS[pick] || '';
}

/**
 * Admin-facing label: seasons collapse to "Best of season".
 * @param {string} pick
 * @returns {string}
 */
function adminLabelForPick(pick) {
  if (EDITORIAL_PICK_SEASONS.includes(pick)) return ADMIN_SEASON_LABEL;
  return labelForPick(pick);
}

/**
 * Meteorological N. hemisphere season from a Date.
 * @param {Date|null|undefined} date
 * @returns {'summer' | 'fall' | 'winter' | 'spring' | null}
 */
export function getEditorialSeasonForDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const month = date.getMonth();
  if (month >= 2 && month <= 4) return EDITORIAL_PICK.SPRING;
  if (month >= 5 && month <= 7) return EDITORIAL_PICK.SUMMER;
  if (month >= 8 && month <= 10) return EDITORIAL_PICK.FALL;
  return EDITORIAL_PICK.WINTER;
}

/**
 * Season for an event from its calendar day.
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {'summer' | 'fall' | 'winter' | 'spring' | null}
 */
export function getEditorialSeasonForEvent(event) {
  return getEditorialSeasonForDate(getEventCalendarDay(event));
}

/**
 * Normalize legacy string or array into a unique ordered pick list.
 * Order: day, month, week?, saveTheDate?, season?
 * Season ⇒ saveTheDate + week ⇒ month ⇒ day. saveTheDate ⇒ month ⇒ day (independent
 * of week). Week ⇒ month ⇒ day (independent of saveTheDate). Month ⇒ day.
 * At most one season.
 * Legacy `year` / `sff` are ignored.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [_event] unused; kept for call-site compatibility
 * @returns {Array<'day' | 'week' | 'month' | 'saveTheDate' | 'summer' | 'fall' | 'winter' | 'spring'>}
 */
export function normalizeEditorialPicks(value, _event) {
  const raw = Array.isArray(value) ? value : value == null || value === '' ? [] : [value];
  const filtered = raw.filter((item) => typeof item === 'string' && VALID_PICKS.has(item));

  const season = EDITORIAL_PICK_SEASONS.find((s) => filtered.includes(s)) || null;
  const hasSeason = Boolean(season);
  const hasWeek = filtered.includes(EDITORIAL_PICK.WEEK) || hasSeason;
  const hasSaveTheDate = filtered.includes(EDITORIAL_PICK.SAVE_THE_DATE) || hasSeason;
  const hasMonth =
    filtered.includes(EDITORIAL_PICK.MONTH) || hasWeek || hasSaveTheDate;
  const hasDay = filtered.includes(EDITORIAL_PICK.DAY) || hasMonth;

  const picks = [];
  if (hasDay) picks.push(EDITORIAL_PICK.DAY);
  if (hasMonth) picks.push(EDITORIAL_PICK.MONTH);
  if (hasWeek) picks.push(EDITORIAL_PICK.WEEK);
  if (hasSaveTheDate) picks.push(EDITORIAL_PICK.SAVE_THE_DATE);
  if (season) picks.push(season);
  return picks;
}

/**
 * Floor an editorial pick at Save the date (cascades month/day). Season and week stay.
 * Used when a super admin saves a public event.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {Array<'day' | 'week' | 'month' | 'saveTheDate' | 'summer' | 'fall' | 'winter' | 'spring'>}
 */
export function ensureSaveTheDatePick(value, event) {
  return normalizeEditorialPicks(
    [...normalizeEditorialPicks(value, event), EDITORIAL_PICK.SAVE_THE_DATE],
    event
  );
}

/**
 * Highest-rank pick for public category display/filter.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {'day' | 'week' | 'month' | 'summer' | 'fall' | 'winter' | 'spring' | 'saveTheDate' | null}
 */
export function getPrimaryEditorialPick(value, event) {
  const picks = normalizeEditorialPicks(value, event);
  // Season implies saveTheDate (cascade), so season must win the tie-break.
  const season = EDITORIAL_PICK_SEASONS.find((s) => picks.includes(s));
  if (season) return season;
  if (picks.includes(EDITORIAL_PICK.SAVE_THE_DATE)) return EDITORIAL_PICK.SAVE_THE_DATE;
  if (picks.includes(EDITORIAL_PICK.WEEK)) return EDITORIAL_PICK.WEEK;
  if (picks.includes(EDITORIAL_PICK.MONTH)) return EDITORIAL_PICK.MONTH;
  if (picks.includes(EDITORIAL_PICK.DAY)) return EDITORIAL_PICK.DAY;
  return null;
}

/**
 * Public category string (specific season names).
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {string}
 */
export function getPrimaryEditorialPickLabel(value, event) {
  const pick = getPrimaryEditorialPick(value, event);
  return pick ? labelForPick(pick) : '';
}

/**
 * Admin Event Database Best-of filter key from primary pick.
 * Maps week → month; seasons → season. Save the date is its own key.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {'day' | 'month' | 'season' | 'saveTheDate' | null}
 */
export function getAdminEditorialPickFilterKey(value, event) {
  const primary = getPrimaryEditorialPick(value, event);
  if (!primary) return null;
  if (primary === EDITORIAL_PICK.SAVE_THE_DATE) return EDITORIAL_PICK.SAVE_THE_DATE;
  if (EDITORIAL_PICK_SEASONS.includes(primary)) return 'season';
  if (primary === EDITORIAL_PICK.MONTH || primary === EDITORIAL_PICK.WEEK) {
    return 'month';
  }
  if (primary === EDITORIAL_PICK.DAY) return 'day';
  return null;
}

/**
 * Multi-select OR filter for admin Best-of buttons.
 * Empty selectedKeys = no pick filter (always match).
 * `unranked` matches events with no pick (null, empty, or ignored legacy year/sff).
 * @param {{ editorialPick?: unknown }|null|undefined} event
 * @param {Array<'day' | 'month' | 'season' | 'saveTheDate' | 'unranked'>} selectedKeys
 * @returns {boolean}
 */
export function eventMatchesAdminEditorialPickFilters(event, selectedKeys) {
  const keys = Array.isArray(selectedKeys) ? selectedKeys : [];
  if (keys.length === 0) return true;
  const filterKey = getAdminEditorialPickFilterKey(event?.editorialPick, event);
  if (!filterKey) return keys.includes('unranked');
  return keys.includes(filterKey);
}

/**
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {boolean}
 */
export function isEditorialPick(value, event) {
  return normalizeEditorialPicks(value, event).length > 0;
}

/**
 * Homepage featured “Save the date” block.
 * Includes stored saveTheDate picks only — other picks stay in date groups.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {boolean}
 */
export function isHomepageFeaturedEditorialPick(value, event) {
  return normalizeEditorialPicks(value, event).includes(EDITORIAL_PICK.SAVE_THE_DATE);
}

/**
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {boolean}
 */
export function hasEditorialMonthPick(value, event) {
  return normalizeEditorialPicks(value, event).includes(EDITORIAL_PICK.MONTH);
}

/**
 * Month / season / Save the date primary — subtle fire chrome on public cards.
 * Day-only and week-only do not get fire.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {boolean}
 */
export function hasEditorialFirePick(value, event) {
  const primary = getPrimaryEditorialPick(value, event);
  if (!primary || primary === EDITORIAL_PICK.DAY || primary === EDITORIAL_PICK.WEEK) {
    return false;
  }
  return true;
}

/**
 * Admin-facing joined labels (seasons show as "Best of season").
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {string}
 */
export function getEditorialPickLabel(value, event) {
  return normalizeEditorialPicks(value, event)
    .map((pick) => adminLabelForPick(pick))
    .filter(Boolean)
    .join(' · ');
}

/**
 * Public featured-section heading.
 * @returns {string}
 */
export function getBestEventsSectionLabel() {
  return SAVE_THE_DATE_LABEL;
}

/**
 * Star count: day → 1, month → 2, week → 3, saveTheDate → 4, season → 5.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {number}
 */
export function getEditorialPickStarCount(value, event) {
  const primary = getPrimaryEditorialPick(value, event);
  return primary ? PICK_SORT_SCORE[primary] : 0;
}

/**
 * Unicode star mark for admin tags / tests.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {string}
 */
export function getEditorialPickStars(value, event) {
  const count = getEditorialPickStarCount(value, event);
  return count ? '★'.repeat(count) : '';
}

/**
 * Admin toggle selection — day / month / Best of season / Save the date.
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {string[]}
 */
export function editorialPickToToggleSelection(value, event) {
  return [...new Set(
    normalizeEditorialPicks(value, event)
      .map((pick) => adminLabelForPick(pick))
      .filter(Boolean)
  )];
}

/**
 * Map admin toggle labels → editorialPick write value.
 * - "Best of season" ⇒ infer season from event date at write time; also flags saveTheDate
 * - season ⇒ saveTheDate + week ⇒ month ⇒ day
 * - Save the date ⇒ month ⇒ day (independent of week)
 * - week ⇒ month ⇒ day (independent of saveTheDate)
 * - clearing day while a higher Best-of tier is on clears the whole cascade
 * - clearing month while week/saveTheDate/season is on clears those too
 * - clearing week (or saveTheDate) while season is on clears season too
 * @param {string[]} selected
 * @param {unknown} [previousValue]
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {Array<string> | null} null when cleared (write deleteField)
 */
export function editorialPickFromToggleSelection(selected, previousValue, event) {
  const labels = Array.isArray(selected) ? selected : [];
  const prev = normalizeEditorialPicks(previousValue, event);
  const prevHadDay = prev.includes(EDITORIAL_PICK.DAY);
  const prevHadMonth = prev.includes(EDITORIAL_PICK.MONTH);
  const prevHadWeek = prev.includes(EDITORIAL_PICK.WEEK);
  const prevHadSaveTheDate = prev.includes(EDITORIAL_PICK.SAVE_THE_DATE);
  const prevHadSeason = EDITORIAL_PICK_SEASONS.some((s) => prev.includes(s));
  const prevHadHigher = prevHadMonth || prevHadWeek || prevHadSaveTheDate || prevHadSeason;

  const labelHasDay = labels.includes(PICK_LABELS[EDITORIAL_PICK.DAY]);
  const labelHasMonth = labels.includes(PICK_LABELS[EDITORIAL_PICK.MONTH]);
  let labelHasWeek = labels.includes(PICK_LABELS[EDITORIAL_PICK.WEEK]);
  let labelHasSeason = labels.includes(ADMIN_SEASON_LABEL);
  let labelHasSaveTheDate = labels.includes(SAVE_THE_DATE_LABEL);

  if (prevHadDay && prevHadHigher && !labelHasDay) {
    return null;
  }

  if ((prevHadSeason || prevHadWeek) && prevHadMonth && !labelHasMonth) {
    labelHasSeason = false;
    labelHasWeek = false;
  }
  if ((prevHadSeason || prevHadSaveTheDate) && prevHadMonth && !labelHasMonth) {
    labelHasSeason = false;
    labelHasSaveTheDate = false;
  }

  if (prevHadSeason && prevHadWeek && !labelHasWeek) {
    labelHasSeason = false;
  }
  if (prevHadSeason && prevHadSaveTheDate && !labelHasSaveTheDate) {
    labelHasSeason = false;
  }

  let hasDay = labelHasDay;
  let hasMonth = labelHasMonth;
  let hasWeek = labelHasWeek;
  let hasSaveTheDate = labelHasSaveTheDate;

  let season = null;
  if (labelHasSeason) {
    season = getEditorialSeasonForEvent(event);
    if (!season) {
      season = EDITORIAL_PICK_SEASONS.find((s) => prev.includes(s)) || null;
    }
  }

  if (season) {
    hasWeek = true;
    hasSaveTheDate = true;
    hasMonth = true;
    hasDay = true;
  } else {
    if (hasWeek) {
      hasMonth = true;
    }
    if (hasSaveTheDate) {
      hasMonth = true;
    }
    if (hasMonth) {
      hasDay = true;
    }
  }

  const picks = [];
  if (hasDay) picks.push(EDITORIAL_PICK.DAY);
  if (hasMonth) picks.push(EDITORIAL_PICK.MONTH);
  if (hasWeek) picks.push(EDITORIAL_PICK.WEEK);
  if (hasSaveTheDate) picks.push(EDITORIAL_PICK.SAVE_THE_DATE);
  if (season) picks.push(season);

  if (picks.length === 0) return null;
  return normalizeEditorialPicks(picks, event);
}

/**
 * Sort weight: season (5) > saveTheDate (4) > week (3) > month (2) > day (1) > none (0)
 * @param {unknown} value
 * @param {Record<string, unknown>|null|undefined} [event]
 * @returns {number}
 */
export function editorialPickSortScore(value, event) {
  const primary = getPrimaryEditorialPick(value, event);
  return primary ? PICK_SORT_SCORE[primary] : 0;
}

/**
 * @param {Array<{ editorialPick?: unknown }>} events
 * @returns {Array<{ editorialPick?: unknown }>}
 */
export function sortEventsByEditorialPick(events) {
  return [...events].sort(
    (a, b) =>
      editorialPickSortScore(b.editorialPick, b) - editorialPickSortScore(a.editorialPick, a)
  );
}

/**
 * Homepage / admin "Save the date" strip: soonest date first, then pick rank.
 * @param {Array<{ editorialPick?: unknown, dateStr?: unknown }>} events
 * @returns {Array<{ editorialPick?: unknown, dateStr?: unknown }>}
 */
export function sortFeaturedEventsByDate(events) {
  return [...events].sort((a, b) => {
    const dateDiff = compareEventsUpcomingThenPast(a, b);
    if (dateDiff !== 0) return dateDiff;
    return editorialPickSortScore(b.editorialPick, b) - editorialPickSortScore(a.editorialPick, a);
  });
}
